import { AnimatePresence, motion } from "framer-motion";
import { useHeaderButton, usePrimaryNav } from "@/content-context";
import { Link } from "react-router";
import { useLocation } from "react-router";
import { useEffect, useRef, useState } from "react";

import { Logo } from "./Logo";
import { MobileNav } from "./MobileNav";

/**
 * Header.
 *
 * Over the homepage hero it is invisible chrome, no bar, no background, just
 * the mark and the links sitting on the photograph. On scroll (and on every
 * interior page) it condenses into a floating pill: hairline border, blurred
 * white, soft shadow. One element, two states, nothing else competing.
 */
export function Header() {
  /*
   * The menu comes from the CMS, and falls back to the committed default.
   *
   * An empty navigation collection is treated as "not seeded yet" rather than
   * as "somebody deleted every menu item", because the second is a decision a
   * person makes one item at a time and the first is what a fresh database
   * looks like. See usePrimaryNav.
   */
  const primaryNav = usePrimaryNav();
  // The button beside the menu, edited with the menu in the panel.
  const headerButton = useHeaderButton();
  const pathname = useLocation().pathname;
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<number | null>(null);
  const triggerRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const isHome = pathname === "/";
  const overHero = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpenMenu(null), [pathname]);

  useEffect(() => {
    if (!openMenu) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const activeTrigger = triggerRefs.current[openMenu];
      setOpenMenu(null);
      activeTrigger?.focus();
    };
    const onPointer = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setOpenMenu(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [openMenu]);

  // Small grace period so the pointer can travel from trigger to panel.
  const scheduleClose = () => {
    closeTimer.current = window.setTimeout(() => setOpenMenu(null), 140);
  };
  const cancelClose = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
  };

  const isActive = (href: string) => pathname === href || pathname.startsWith(`${href}/`);

  const linkTone = overHero ? "text-white/75 hover:text-white" : "text-ink/65 hover:text-ink";
  const activeTone = overHero ? "text-white" : "text-navy";
  // Soft pill on hover rather than an underline, so the nav matches the rounded
  // geometry of the header, the buttons and the dropdown panel.
  const hoverPill = overHero ? "hover:bg-white/12" : "hover:bg-mist";
  const openPill = overHero ? "bg-white/12" : "bg-mist";

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div className="container-page">
        <div
          className={`flex items-center justify-between gap-6 transition-[background-color,border-color,box-shadow,padding,margin,border-radius] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            overHero
              ? "mt-0 rounded-none border border-transparent py-5 pl-0 pr-0"
              : "mt-3 rounded-full border border-line bg-white/92 py-2 pl-5 pr-2 shadow-[0_12px_34px_-24px_rgba(12,29,51,0.4)] backdrop-blur-xl"
          }`}
        >
          <Link
            to="/"
            aria-label="Himshikhara Socio-Cultural Society, home"
            className="shrink-0 rounded-lg"
          >
            <Logo tone={overHero ? "light" : "dark"} />
          </Link>

          <div ref={navRef} className="hidden items-center gap-1 lg:flex">
            <nav aria-label="Primary">
              <ul className="flex items-center gap-0.5">
                {primaryNav.map((entry, index) => {
                  const active = isActive(entry.href);

                  if (!entry.children) {
                    return (
                      <li key={entry.href}>
                        <Link
                          to={entry.href}
                          aria-current={active ? "page" : undefined}
                          className={`inline-flex min-h-11 items-center rounded-full px-4 text-[0.875rem] font-medium transition-colors duration-300 ${
                            active ? `${activeTone} ${openPill}` : `${linkTone} ${hoverPill}`
                          }`}
                        >
                          {entry.label}
                        </Link>
                      </li>
                    );
                  }

                  const open = openMenu === entry.label;
                  return (
                    <li
                      key={entry.href}
                      className="relative"
                      onMouseEnter={() => {
                        cancelClose();
                        setOpenMenu(entry.label);
                      }}
                      onMouseLeave={scheduleClose}
                    >
                      <button
                        ref={(element) => {
                          triggerRefs.current[entry.label] = element;
                        }}
                        type="button"
                        aria-expanded={open}
                        aria-haspopup="true"
                        aria-current={active ? "page" : undefined}
                        aria-controls={`nav-menu-${index}`}
                        onClick={() => setOpenMenu(open ? null : entry.label)}
                        onKeyDown={(event) => {
                          if (event.key !== "Enter" && event.key !== " " && event.key !== "ArrowDown") return;
                          event.preventDefault();
                          setOpenMenu(entry.label);
                          window.requestAnimationFrame(() => {
                            document
                              .getElementById(`nav-menu-${index}`)
                              ?.querySelector<HTMLElement>("a[href]")
                              ?.focus();
                          });
                        }}
                        className={`inline-flex min-h-11 items-center gap-1.5 rounded-full px-4 text-[0.875rem] font-medium transition-colors duration-300 ${
                          active || open ? activeTone : linkTone
                        } ${open ? openPill : hoverPill}`}
                      >
                        {entry.label}
                        <svg
                          aria-hidden="true"
                          viewBox="0 0 10 10"
                          className={`h-2 w-2 opacity-60 transition-transform duration-300 ${
                            open ? "rotate-180" : ""
                          }`}
                        >
                          <path
                            d="M1.5 3.5 5 7l3.5-3.5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>

                      <AnimatePresence>
                        {open ? (
                          <motion.div
                            id={`nav-menu-${index}`}
                            initial={{ opacity: 0, y: 6, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 4, scale: 0.99 }}
                            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                            className={`absolute top-full w-[min(23rem,calc(100vw-3rem))] pt-3 ${
                              index >= primaryNav.length - 2 ? "right-0" : "left-0"
                            }`}
                            onMouseEnter={cancelClose}
                            onMouseLeave={scheduleClose}
                          >
                            <div className="overflow-hidden rounded-[20px] border border-line bg-white/95 p-2 shadow-[0_28px_60px_-32px_rgba(12,29,51,0.45)] backdrop-blur-xl">
                              {entry.children.map((child) => (
                                <Link
                                  key={child.href}
                                  to={child.href}
                                  className="group/item block rounded-[14px] px-4 py-3 transition-colors duration-300 hover:bg-mist"
                                >
                                  <span className="flex items-center justify-between gap-3">
                                    <span className="text-[0.9375rem] font-semibold text-ink">
                                      {child.label}
                                    </span>
                                    <svg
                                      aria-hidden="true"
                                      viewBox="0 0 16 16"
                                      className="h-3.5 w-3.5 shrink-0 -translate-x-1 text-blue opacity-0 transition-[opacity,transform] duration-300 group-hover/item:translate-x-0 group-hover/item:opacity-100"
                                    >
                                      <path
                                        d="M3 8h9M8.5 4.5 12 8l-3.5 3.5"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  </span>
                                  {child.description ? (
                                    <span className="mt-0.5 block text-[0.8125rem] leading-snug text-muted">
                                      {child.description}
                                    </span>
                                  ) : null}
                                </Link>
                              ))}

                              <Link
                                to={entry.href}
                                className="mt-1 block rounded-[14px] px-4 py-2.5 text-[0.8125rem] font-semibold text-blue transition-colors duration-300 hover:bg-mist"
                              >
                                {entry.overviewLabel ?? `View all ${entry.label.toLowerCase()}`}
                              </Link>
                            </div>
                          </motion.div>
                        ) : null}
                      </AnimatePresence>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {headerButton ? (
              <Link
                to={headerButton.href}
                className={`ml-2 inline-flex min-h-11 items-center rounded-full px-5 text-[0.875rem] font-semibold transition-[background-color,border-color,color] duration-300 ${
                  overHero
                    ? "border border-white/30 text-white hover:border-white/70 hover:bg-white/10"
                    : "bg-navy text-white hover:bg-midnight"
                }`}
              >
                {headerButton.label}
              </Link>
            ) : null}
          </div>

          <MobileNav overHero={overHero} />
        </div>
      </div>
    </header>
  );
}
